import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

type PageState = 'loading' | 'done' | 'error';
type Provider = 'toss' | 'pg';

export default function BillingPaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState<PageState>('loading');
  const [err, setErr] = useState<string | null>(null);
  const requestedRef = useRef(false);

  const API_BASE_URL =
    window.location.hostname === 'localhost'
      ? 'http://localhost:8080/api'
      : 'https://www.policy-intel.com/api';

  useEffect(() => {
    const run = async () => {
      if (requestedRef.current) return;
      requestedRef.current = true;

      try {
        const provider = (sp.get('provider') || 'toss') as Provider;

        if (provider === 'toss') {
          const paymentKey = sp.get('paymentKey')?.trim() || '';
          const orderId = sp.get('orderId')?.trim() || '';
          const amountStr = sp.get('amount')?.trim() || '';
          const amount = Number(amountStr);

          if (!paymentKey || !orderId || !amountStr || !Number.isFinite(amount)) {
            throw new Error('토스 결제 파라미터가 누락되었거나 올바르지 않습니다.');
          }

          const payload = {
            provider: 'toss',
            paymentKey,
            orderId,
            amount,
          };

          const res = await fetch(`${API_BASE_URL}/billing/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload),
          });

          let responseData: any = null;
          try {
            responseData = await res.json();
          } catch (_) {
            responseData = null;
          }

          if (!res.ok) {
            throw new Error(
              responseData?.message ||
                responseData?.error ||
                `결제 승인 처리 실패 (${res.status})`,
            );
          }

          setState('done');
          return;
        }

        if (provider === 'pg') {
          const invoiceId = sp.get('invoiceId') || '';
          const orderId = sp.get('orderId') || sp.get('merchant_uid') || '';
          const paymentId =
            sp.get('paymentId') || sp.get('imp_uid') || sp.get('transactionId') || '';
          const amountRaw = sp.get('amount');
          const amount = amountRaw ? Number(amountRaw) : null;

          const payload = {
            provider: 'pg',
            invoiceId: invoiceId || null,
            orderId: orderId || null,
            paymentId: paymentId || null,
            amount,
            query: Object.fromEntries(sp.entries()),
          };

          const res = await fetch(`${API_BASE_URL}/billing/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload),
          });

          let responseData: any = null;
          try {
            responseData = await res.json();
          } catch (_) {
            responseData = null;
          }

          if (!res.ok) {
            throw new Error(
              responseData?.message ||
                responseData?.error ||
                `결제 승인 처리 실패 (${res.status})`,
            );
          }

          setState('done');
          return;
        }

        throw new Error('지원하지 않는 결제 provider 입니다.');
      } catch (e: any) {
        console.error('[BillingPaymentSuccess] confirm error =', e);
        setErr(e?.message ?? '결제 승인 처리 실패');
        setState('error');
      }
    };

    void run();
  }, [sp, API_BASE_URL]);

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', p: 3 }}>
      {state === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography>결제 승인 처리 중…</Typography>
        </Box>
      )}

      {state === 'done' && (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            결제가 완료되었습니다. Pro 플랜이 활성화되었습니다.
          </Alert>
          <Button
            variant="contained"
            onClick={() => nav('/mypage?tab=billing&refresh=1', { replace: true })}
          >
            마이페이지로 돌아가기
          </Button>
        </>
      )}

      {state === 'error' && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {err ?? '결제 처리 실패'}
          </Alert>
          <Button
            variant="outlined"
            onClick={() => nav('/mypage?tab=billing', { replace: true })}
          >
            마이페이지로 돌아가기
          </Button>
        </>
      )}
    </Box>
  );
}